"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Button, Divider } from "antd";
import Banner from "@/components/shared/Banner";
import ProductSlider from "./products/Slide";
import NewsSlide from "./news/Slide";
import PartnersSlide from "./partners/Slide";
import SlugGeneration from "@/lib/slugGeneration";
import Video from "./Video";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("vi-VN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export default function HomeIndex({
  banners,
  setting,
  services,
  projects,
  news: dataNews,
  partners,
}) {
  const bannersSliderRef = useRef(null);
  const statsRef = useRef(null);
  const aboutUs = JSON.parse(setting?.aboutUs || "{}");
  const [thongKe, setThongKe] = useState({
    kinhNghiem: 0,
    duAn: 0,
    khachHang: 0,
  });
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!statsRef.current || hasAnimated) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);

          const target = {
            kinhNghiem: parseInt(aboutUs.thongKe?.kinhNghiem) || 0,
            duAn: parseInt(aboutUs.thongKe?.soDuAn) || 0,
            khachHang: parseInt(aboutUs.thongKe?.soKhachHang) || 0,
          };

          const duration = 1000; // 2 seconds
          const startTime = Date.now();

          const animateCounter = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);

            setThongKe({
              kinhNghiem: Math.floor(target.kinhNghiem * progress),
              duAn: Math.floor(target.duAn * progress),
              khachHang: Math.floor(target.khachHang * progress),
            });

            if (progress < 1) {
              requestAnimationFrame(animateCounter);
            }
          };

          animateCounter();
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [hasAnimated, aboutUs?.thongKe]);

  const swiperBannersOption = {
    effect: "fade",
    autoplay: {
      delay: 3000,
    },
    fadeEffect: { crossFade: true },
    pagination: {
      clickable: true,
    },
    speed: 3000,
  };

  const onBannersSliderPrev = () => {
    if (bannersSliderRef.current) {
      bannersSliderRef.current.slidePrev();
    }
  };

  const onBannersSliderNext = () => {
    if (bannersSliderRef.current) {
      bannersSliderRef.current.slideNext();
    }
  };

  const convertSlug = (text) => {
    return SlugGeneration(text);
  };

  const highLightNew = dataNews.find((item) => item.isHighLight);
  const news = dataNews.filter((item) => !item.isHighLight);

  return (
    <div className="overflow-hidden">
      <div className="relative">
        <button
          type="button"
          className="container md:block xl:px-16 xxl:px-15 hidden px-2 z-10 absolute top-1/2 -translate-y-1/2 cursor-pointer border-none"
          onClick={onBannersSliderPrev}
          aria-label="Previous slide"
        >
          <div className="w-12 h-12 p-3 bg-[#FFFFFF66] rounded-full">
            <img
              className="w-6 h-6 object-contain"
              src="/svg/arrow-left.svg"
              alt="prev"
              width={44}
              height={44}
            />
          </div>
        </button>
        <button
          type="button"
          className="md:block hidden xl:px-16 xxl:px-15 px-2 z-10 absolute top-1/2 -translate-y-1/2 right-0 cursor-pointer border-none"
          onClick={onBannersSliderNext}
          aria-label="Next slide"
        >
          <div className=" p-3 bg-[#FFFFFF66] rounded-full">
            <img
              className="w-6 h-6 object-contain"
              src="/svg/arrow-right.svg"
              alt="next"
              width={44}
              height={44}
            />
          </div>
        </button>
        {banners && banners.length > 0 && (
          <Banner
            ref={bannersSliderRef}
            slides={banners}
            className="slider__navigation-arrow w-full"
            options={swiperBannersOption}
            controls={{ withPagination: true, withNavigation: false }}
          >
            {({ slide: { item } }) => (
              <div className="w-screen h-[360px] sm:h-[500px] xl:h-screen">
                <img
                  className="h-full w-full object-cover place-content-center"
                  src={item.thumbnail || "/images/default.jpg"}
                  alt=""
                />
                <div className="absolute top-1/2 -translate-y-1/2 left-4 md:left-16 xl:left-[200px] flex flex-col justify-start items-start px-4 md:px-0">
                  <div
                    data-aos="fade-right"
                    data-aos-anchor-placement="top"
                    className="text-white uppercase text-2xl sm:text-4xl md:text-5xl xl:text-[65px] font-extrabold leading-tight md:leading-[82px]"
                  >
                    Kiến Tạo Không Gian
                  </div>
                  <div
                    data-aos="fade-right"
                    data-aos-anchor-placement="top"
                    className="uppercase text-2xl sm:text-4xl md:text-5xl xl:text-[65px] font-extrabold leading-tight md:leading-[100px]"
                    style={{
                      background:
                        "linear-gradient(90deg, #FFB900 0%, #E17100 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Sống đẳng cấp
                  </div>
                  <div
                    data-aos="fade-right"
                    data-aos-anchor-placement="top"
                    className="relative inline-flex transition-all mt-4 md:mt-10 border border-white text-white duration-300 rounded-full py-2 md:py-[14px] px-4 md:px-8 hover:bg-[#E5C383] hover:text-white hover:border-none text-sm md:text-base"
                  >
                    <Link href="/dich-vu" className="text-white">
                      Tìm hiểu thêm
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </Banner>
        )}
        <div className="w-full h-0.5 bg-orange-90" />
      </div>

      <div
        id="ab_us"
        className="ab-us bg-[#F1FAFE] xl:h-screen w-full flex xxl:flex-row flex-col justify-between items-center overflow-hidden relative gap-y-8"
      >
        <div className="container grid lg:grid-cols-12 items-center lg:h-screen us_info xl:px-0 lg:px-6 px-4 py-10 lg:py-0 p-[30px]">
          <div className="bg-[url('/images/logo_white_large.png')] w-auto h-auto xl:w-[664px] xl:h-[628px] xl:left-[500px] top-0 absolute bg-no-repeat hidden lg:block" />
          <div
            data-aos="fade-right"
            data-aos-anchor-placement="top"
            className="col-span-12 lg:col-span-5 lg:z-10"
          >
            <p className="text-[#2CADE3] text-sm md:text-lg font-semibold uppercase mb-2 md:mb-[15px]">
              new information
            </p>
            <div>
              <span className="font-bold text-2xl md:text-3xl xl:text-6xl -mb-2 mr-4">
                Về <span className="text-[#E5C383]">Chúng Tôi</span>
              </span>
            </div>
            <div className="flex flex-col gap-y-4 md:gap-y-6 mt-4 md:mt-8 mb-4 md:mb-12 font-normal text-base md:text-xl xl:text-2xl">
              <div
                className="text-justify text-[#546475]"
                dangerouslySetInnerHTML={{
                  __html: aboutUs?.veChungToiMoTa || "",
                }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 md:gap-4" ref={statsRef}>
              <div
                className="py-3 md:py-[26px] px-2 md:px-[18px] bg-white rounded-lg"
                style={{ boxShadow: "0px 4px 29.3px 0px #00000040" }}
              >
                <img
                  src="/svg/reward.svg"
                  className="w-6 md:w-auto"
                  alt="Kinh nghiệm"
                />
                <h2 className="mt-2 md:mt-[28px] text-lg md:text-[28px] font-bold">
                  {thongKe?.kinhNghiem || "0"}+
                </h2>
                <p className="mt-1 md:mt-[14px] text-[#323232] text-xs md:text-xl">
                  Kinh nghiệm
                </p>
              </div>
              <div
                className="py-3 md:py-[26px] px-2 md:px-[18px] bg-white rounded-lg"
                style={{ boxShadow: "0px 4px 29.3px 0px #00000040" }}
              >
                <img
                  src="/svg/user-setting.svg"
                  className="w-6 md:w-auto"
                  alt="Dự án"
                />
                <h2 className="mt-2 md:mt-[28px] text-lg md:text-[28px] font-bold">
                  {thongKe?.duAn || "0"}+
                </h2>
                <p className="mt-1 md:mt-[14px] text-[#323232] text-xs md:text-xl">
                  Dự án
                </p>
              </div>
              <div
                className="py-3 md:py-[26px] px-2 md:px-[18px] bg-white rounded-lg"
                style={{ boxShadow: "0px 4px 29.3px 0px #00000040" }}
              >
                <img
                  src="/svg/user-like.svg"
                  className="w-6 md:w-auto"
                  alt="Khách hàng"
                />
                <h2 className="mt-2 md:mt-[28px] text-lg md:text-[28px] font-bold">
                  {thongKe?.khachHang || "0"}+
                </h2>
                <p className="mt-1 md:mt-[14px] text-[#323232] text-xs md:text-xl">
                  Khách hàng
                </p>
              </div>
            </div>
          </div>
        </div>
        <div
          data-aos="fade-left"
          className="skew-box lg:absolute top-0 lg:flex grid grid-cols-3 right-6 text-white xl:text-4xl text-base font-extrabold lg:w-auto w-full lg:h-full"
        >
          <Link
            href="/ve-chung-toi"
            style={{
              backgroundImage: `url(${aboutUs?.giaTriCotLoi?.[0]?.hinhAnh || "/images/test/2.jpg"})`,
            }}
            className={`lg:w-44 xl:w-62 xxl:w-80 w-full h-32 sm:h-40 md:h-60 lg:h-full bg-center bg-cover lg:-skew-x-6 overflow-hidden flex justify-center items-end relative skew-item`}
          >
            <div className="flex md:flex-row flex-col gap-x-2 xl:gap-x-3 items-center z-20 py-2 md:py-4 xl:py-16 px-0 xl:px-4 skew-text">
              <div>
                <div className="text-xs sm:text-sm md:text-base xl:text-2xl uppercase whitespace-nowrap font-bold xxl:text-3xl">
                  {aboutUs?.giaTriCotLoi[0]?.ten}
                </div>
              </div>
            </div>
            <div className="absolute top-0 right-0 bottom-0 left-0 opacity-0 z-10 bg-hover skew-mask w-full" />
          </Link>
          <Link
            href="/ve-chung-toi"
            style={{
              backgroundImage: `url(${aboutUs?.giaTriCotLoi?.[1]?.hinhAnh || "/images/test/3.jpg"})`,
            }}
            className={` lg:w-44 xl:w-62 xxl:w-80 w-full h-32 sm:h-40 md:h-60 lg:h-full bg-center bg-cover lg:-skew-x-6 overflow-hidden flex justify-center items-end relative skew-item`}
          >
            <div className="flex md:flex-row flex-col items-center gap-x-2 xl:gap-x-3 justify-center z-20 py-2 md:py-4 xl:py-16 px-2 xl:px-4 skew-text">
              <div>
                <div className="text-xs sm:text-sm md:text-base xl:text-2xl uppercase whitespace-nowrap font-bold xxl:text-3xl">
                  {aboutUs?.giaTriCotLoi[1]?.ten}
                </div>
              </div>
            </div>
            <div className="absolute top-0 right-0 bottom-0 left-0 opacity-0 z-10 bg-hover skew-mask w-full" />
          </Link>
          <Link
            href="/ve-chung-toi"
            style={{
              backgroundImage: `url(${aboutUs?.giaTriCotLoi?.[2]?.hinhAnh || "/images/test/4.jpg"})`,
            }}
            className={` lg:w-44 xl:w-62 xxl:w-80 w-full h-32 sm:h-40 md:h-60 lg:h-full bg-center bg-cover lg:-skew-x-6 overflow-hidden flex justify-center items-end relative skew-item`}
          >
            <div className="flex md:flex-row flex-col gap-x-2 xl:gap-x-3 items-center z-20 py-2 md:py-4 xl:py-16 px-2 xl:px-4 skew-text">
              <div>
                <div className="text-xs sm:text-sm md:text-base xl:text-2xl uppercase whitespace-nowrap font-bold xxl:text-3xl">
                  {aboutUs?.giaTriCotLoi[2]?.ten}
                </div>
              </div>
            </div>
            <div className="absolute top-0 right-0 bottom-0 left-0 opacity-0 z-10 bg-hover skew-mask w-full" />
          </Link>
        </div>
      </div>
      <Video aboutUs={aboutUs} />

      <div
        id="services"
        className="bg-[url('/images/test/1.png')] bg-cover bg-center bg-no-repeat h-fit flex flex-col justify-between relative"
      >
        <div className="bg-[#0C1B56CC] pt-10 xl:pt-16 pb-10 md:pb-32">
          <div className="container flex flex-col items-center justify-center gap-y-6 xl:gap-y-10 xxl:gap-y-20 justify-self-center px-4 xl:px-0">
            <div
              data-aos="fade-down"
              data-aos-anchor-placement="top"
              className="text-white text-center"
            >
              <Link
                href="/dich-vu"
                className="!text-white font-semibold text-2xl sm:text-3xl md:text-4xl xl:text-[42px]"
              >
                Dịch Vụ Của <span className="text-[#E5C383]">Chúng Tôi</span>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-1 gap-y-1 overflow-hidden w-full">
              {services?.slice(0, 4).map((service, index) => (
                <Link
                  key={service.id}
                  href={`/dich-vu/#${service.slug}`}
                  data-aos="fade-left"
                  data-aos-duration="2000"
                  data-aos-delay={index === 0 ? 100 : index * 100}
                  data-aos-anchor-placement="top"
                  style={{ backgroundImage: `url(${service.thumbnail})` }}
                  className="group-service transition-all duration-300 bg-no-repeat bg-cover w-full h-[300px] sm:h-[350px] md:h-[450px] xl:h-[520px] text-white flex flex-col overflow-hidden gap-y-4 md:gap-y-6 justify-center p-4 md:p-8 relative"
                >
                  <div className="relative transition-all duration-300 text-xl sm:text-2xl md:text-3xl text-center font-bold self-center uppercase text-white">
                    {service.name}
                  </div>
                  <div className="description text-center text-white text-sm md:text-lg font-normal self-center">
                    {service.description}
                  </div>
                  <div className="button relative transition-all border border-white text-white duration-300 rounded-full py-2 md:py-[14px] px-4 md:px-8 self-center text-sm md:text-base">
                    Tìm hiểu thêm
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="relative bg-[#0C1B56] xl:bg-transparent">
        <div
          data-aos="fade-up"
          data-aos-duration="1000"
          className="relative mb-5 xl:mb-0 xl:absolute xl:top-8 xl:right-16 z-40 py-6 md:py-0 px-4 xl:px-0"
        >
          <Link
            href="/du-an"
            className="font-semibold text-center block text-2xl sm:text-3xl md:text-5xl xl:text-6xl !text-white"
          >
            Các dự án <span className="text-[#E5C383]">nổi bật</span>
          </Link>
          <div className="text-center xl:text-end text-white xl:text-white text-sm md:text-base">
            Của chúng tôi
          </div>
        </div>
        <div className="hidden xl:grid grid-cols-12 items-center overflow-hidden relative px-2 lg:p-0 h-auto lg:h-[800px] w-full gap-y-1">
          {projects
            .reverse()
            .slice(0, 3)
            .map((project, index) => (
              <Link
                key={project.id}
                href={`/du-an/${project.id}`}
                className="col-span-3 relative lg:w-auto w-full h-full"
                style={{
                  zIndex: `${30 - index * 10}`,
                  left: `-${(index + 3) * 100}px`,
                }}
                data-aos="fade-right"
                data-aos-duration="1000"
                data-aos-delay={(index + 1) * 300}
              >
                <div className="group hover:scale-110 transition-all duration-300 absolute h-[1500px] w-[700px] top-[-350px] rounded-r-[700px] overflow-hidden after:content-[''] after:duration-300 after:absolute after:w-full after:h-1/2 after:bottom-0 after:left-0 after:z-[0] after:bg-gradient-to-t after:from-[#0055aa]">
                  <img
                    className="block w-full h-full object-cover"
                    src={project.thumbnail}
                    alt={project.name}
                    loading="lazy"
                  />
                </div>
                <div className="absolute bottom-5 xl:-right-56 xxl:-right-52 w-2/3 text-white flex flex-col items-start gap-y-8">
                  {/* <img
                  src="/images/icons/right-arrow.png"
                  className="w-auto"
                  alt="/"
                  width={40}
                  height={40}
                /> */}
                  <span className="text-base xxl:text-xl font-medium relative right-0 xxl:max-w-[75%]">
                    {project.name}
                  </span>
                </div>
              </Link>
            ))}
          <div
            data-aos="zoom-in-up"
            data-aos-duration="1000"
            data-aos-delay="900"
            className="absolute right-0 w-full h-full"
          >
            <img
              className="block w-full h-full object-cover"
              src={projects[0]?.thumbnail || "/images/test/p4.jpg"}
              alt="/"
            />
          </div>
        </div>
        <div className="flex flex-col gap-y-1 xl:hidden">
          {projects.map((project) => (
            <Link
              key={project.id}
              href={`/du-an/${project.id}`}
              data-aos="zoom-in"
              data-aos-duration="1000"
              className="group overflow-hidden relative after:content-[''] after:duration-300 after:absolute after:w-full after:h-1/2 after:bottom-0 after:left-0 after:z-[0] after:bg-gradient-to-t after:from-prim-70"
            >
              <img
                className="w-full object-cover h-[400px] lg:h-[600px] group-hover:scale-110 transition-all duration-300"
                src={project.thumbnail}
                alt={project.name}
                width={800}
                height={600}
              />
              <div className="absolute bottom-5 w-full text-white flex flex-col items-start z-[1] gap-y-8 px-5">
                <span className="md:text-xl font-medium relative right-0">
                  {project.name}
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="hidden xl:flex items-center gap-x-4 justify-self-end absolute xl:right-16 xl:bottom-8">
          <Link
            href="/du-an"
            className="text-black-100 xl:!text-white flex-none xl:font-medium font-normal xl:text-xl"
          >
            Xem tất cả dự án
          </Link>
          <Divider />
        </div>
      </div>

      {/* News Section */}
      <div
        id="news"
        className="bg-white xl:h-full h-full w-screen flex justify-center relative"
      >
        <div className="bg-[url('/images/news/EIH_NEWs.png')] bg-center bg-cover bg-no-repeat w-[280px] md:w-[600px] h-10 md:h-20 absolute bottom-0 md:bottom-[400px] left-14 md:left-[420px] xl:block hidden" />
        <div className="container xl:px-0 px-4 mt-7 md:mt-0 h-fit">
          <div
            data-aos="fade-right"
            data-aos-duration="1000"
            data-aos-anchor-placement="top"
            data-aos-delay="200"
            className="mt-0 md:mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <Link
              href="/tin-tuc"
              className="text-2xl sm:text-3xl md:text-4xl xl:text-6xl font-semibold text-[#040404]"
            >
              Tin tức <span className="text-[#E5C383]">Nổi Bật</span>
            </Link>
            <Link href="/tin-tuc">
              <Button
                size="large"
                shape="round"
                type="primary"
                className="!py-3 md:!py-4 !px-6 md:!px-10 !h-[44px] md:!h-[50px] !text-black !font-semibold !text-sm md:!text-base"
              >
                Xem tất cả
              </Button>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-x-8 xl:gap-x-20 gap-y-4 mt-8 md:mt-14 p-[30px]">
            <div
              data-aos="fade-right"
              data-aos-duration="1000"
              data-aos-delay="400"
              data-aos-anchor-placement="top"
              className="flex flex-col gap-y-3 md:gap-y-7"
            >
              <div className="flex items-center gap-x-2">
                <img
                  src="/svg/clock.svg"
                  alt="clock"
                  className="w-4 md:w-auto"
                />
                <span className="font-light text-[#06B6D4] text-sm md:text-base">
                  {formatDate(highLightNew.createdAt)}
                </span>
              </div>
              <Link
                href={`/tin-tuc/${highLightNew.id}`}
                className="font-semibold text-xl sm:text-2xl md:text-3xl xl:text-5xl xl:leading-15 !text-[#303133]"
              >
                {highLightNew.title}
              </Link>
              <div className="text-sm md:text-xl font-normal text-justify text-[#606266]">
                {highLightNew.description}
              </div>
            </div>
            <div
              data-aos="fade-left"
              data-aos-duration="1000"
              data-aos-delay="400"
              data-aos-anchor-placement="top"
              className="overflow-hidden relative z-[1] rounded-lg"
            >
              <img
                src={highLightNew.thumbnail}
                className="aspect-9/5 w-full h-full object-cover hover:scale-125 duration-1000"
                alt={highLightNew.title}
                width={600}
                height={400}
              />
            </div>
          </div>

          <NewsSlide
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="400"
            data-aos-anchor-placement="top"
            news={news}
            className="xl:mt-3 mt-6 relative z-[1]"
          />
        </div>
      </div>

      <div className="bg-[url('/images/test/bg.png')] relative xl:bg-top bg-cover bg-center bg-no-repeat w-full overflow-hidden flex flex-col justify-center items-center py-10 md:py-[100px] md:pt-[150px]">
        <div
          id="partners"
          className="container relative z-0 overflow-hidden flex flex-col items-center gap-y-6 md:gap-y-16 px-4 xl:px-0"
        >
          <div
            data-aos="fade-left"
            data-aos-anchor-placement="top"
            className="text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-semibold text-[#040404] text-center"
          >
            Đối tác <span className="text-[#E5C383]">của chúng tôi</span>
          </div>
          <div
            data-aos="zoom-in"
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-6 w-full"
          >
            {partners.map((partner) => (
              <div
                key={partner.id}
                className="py-3 md:py-4 px-4 md:px-[52px] rounded-md bg-white flex items-center justify-center"
              >
                <img
                  src={partner.thumbnail}
                  alt={partner.title}
                  className="object-contain w-full h-8 md:h-auto"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-[url('/images/test/l1.jpg')] relative bg-center bg-cover bg-no-repeat w-full overflow-hidden">
        <div className="bg-[#051641CC] flex flex-col justify-center items-center py-10 md:py-[100px] px-4">
          <div className="container relative z-0 overflow-hidden flex flex-col items-center gap-y-6 md:gap-y-16">
            <div data-aos="fade-up" className="text-center">
              <h2 className="text-2xl md:text-4xl xl:text-6xl font-semibold text-white">
                Sẵn sàng bắt đầu dự án của bạn?
              </h2>
              <p className="text-base md:text-lg xl:text-xl text-[#D1D5DC] mt-4 md:mt-[23px] max-w-2xl mx-auto">
                Liên hệ với chúng tôi ngay hôm nay để nhận tư vấn miễn phí từ
                đội ngũ chuyên gia.
              </p>
              <div className="mt-6 md:mt-[33px] flex flex-col sm:flex-row gap-4 mx-auto justify-center">
                <Link href={`/lien-he#tu-van`}>
                  <Button
                    size="large"
                    shape="round"
                    type="primary"
                    className="!py-3 md:!py-4 !px-6 md:!px-10 !h-[44px] md:!h-[50px] !text-black !font-medium !text-sm md:!text-base"
                  >
                    Nhận tư vấn ngay
                  </Button>
                </Link>
                <a href={`tel:${aboutUs?.soDienThoai}`}>
                  <Button
                    size="large"
                    shape="round"
                    ghost
                    className="!py-3 md:!py-4 !px-6 md:!px-10 !h-[44px] md:!h-[50px] !text-white !font-medium !border-[2px] !text-sm md:!text-base"
                  >
                    Gọi: {aboutUs?.soDienThoai || ""}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
